/** Fiscal → Documentos fiscais: lista, pendências, pedidos de cancelamento, inutilização e XML. */
import {
  FISCAL_DOCUMENT_STATUSES,
  FISCAL_USES,
  FISCAL_USE_LABEL,
  formatMoney,
  type FiscalDocumentStatus,
  type FiscalDocumentSummary,
  type FiscalUse,
  type Page,
} from "@cafegestor/shared";
import { useState } from "react";
import { useCan } from "@/app/me";
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { EmptyState, ErrorState } from "@/components/patterns";
import { apiDownload, errorMessage } from "@/lib/api";
import { Pager, SaveError, SearchInput } from "../cadastros/widgets";
import { dateBR, dateTimeBR, today } from "../estoque/lib";
import { DocStatusBadge, DocumentDetail } from "./DocumentDetail";
import { docStatusLabel, useFiscalAction, useFiscalQuery } from "./lib";

type CancelRequestRow = {
  id: string;
  documentId: string;
  model: string;
  series: number;
  number: number;
  totalAmount: string;
  recipientName: string | null;
  reason: string;
  status: string;
  requestedByEmail: string | null;
  requestedAt: string;
};

export function DocumentosPage() {
  const can = useCan();
  const [status, setStatus] = useState<FiscalDocumentStatus | "todos" | "pendencias">("todos");
  const [use, setUse] = useState<FiscalUse | "">("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const params = new URLSearchParams({ status, limit: "50", offset: String(offset) });
  if (use) params.set("use", use);
  if (q.trim()) params.set("q", q.trim());
  const list = useFiscalQuery<Page<FiscalDocumentSummary>>(`/documentos?${params.toString()}`);
  const requests = useFiscalQuery<CancelRequestRow[]>(
    "/pedidos-cancelamento?status=pendente",
    can("fiscal.documento.cancelar"),
  );

  return (
    <div className="space-y-6">
      <PageTitle
        title="Documentos fiscais"
        description="NF-e e NFC-e emitidas pela fábrica e pelos vendedores. A emissão nunca movimenta estoque ou financeiro; situação incerta é sempre consultada antes de qualquer reenvio."
      />
      {open && <DocumentDetail id={open} onClose={() => setOpen(null)} />}
      {can("fiscal.documento.cancelar") && (requests.data?.length ?? 0) > 0 && (
        <CancelRequests rows={requests.data!} onOpen={setOpen} />
      )}
      <Card>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <SearchInput
            placeholder="Número, chave, cliente ou pedido"
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
          />
          <Select
            aria-label="Situação"
            className="w-56"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as typeof status);
              setOffset(0);
            }}
          >
            <option value="todos">Todas as situações</option>
            <option value="pendencias">Com pendência</option>
            {FISCAL_DOCUMENT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {docStatusLabel(s)}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Ponto de emissão"
            className="w-64"
            value={use}
            onChange={(e) => setUse(e.target.value as FiscalUse | "")}
          >
            <option value="">Todos os pontos de emissão</option>
            {FISCAL_USES.map((u) => (
              <option key={u} value={u}>
                {FISCAL_USE_LABEL[u]}
              </option>
            ))}
          </Select>
        </div>
        {list.error ? (
          <ErrorState message={errorMessage(list.error)} />
        ) : list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table head={["Nº", "Emissão", "Ponto", "Destinatário", "Total", "Situação", ""]}>
              {list.data?.items.map((d) => (
                <tr key={d.id}>
                  <td className="px-3 py-2 font-mono">
                    {d.model === "65" ? "NFC-e" : "NF-e"} {d.series}-{d.number}
                  </td>
                  <td className="px-3 py-2">{dateBR(d.issueDate)}</td>
                  <td className="px-3 py-2">{FISCAL_USE_LABEL[d.useKey]}</td>
                  <td className="px-3 py-2">{d.recipientName ?? "—"}</td>
                  <td className="px-3 py-2 text-right">{formatMoney(d.totalAmount)}</td>
                  <td className="px-3 py-2">
                    <DocStatusBadge status={d.status} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setOpen(d.id)}>
                      Abrir
                    </Button>
                  </td>
                </tr>
              ))}
            </Table>
            {list.data?.items.length === 0 && (
              <div className="mt-4">
                <EmptyState
                  title="Nenhum documento fiscal."
                  description={
                    q.trim() || status !== "todos" || use
                      ? "Mude a pesquisa ou os filtros para ver outros documentos."
                      : "As notas aparecem aqui quando uma venda, carga ou devolução pede a emissão."
                  }
                />
              </div>
            )}
            <Pager total={list.data?.total ?? 0} offset={offset} onOffset={setOffset} />
          </>
        )}
      </Card>
      <div className="grid gap-6 lg:grid-cols-2">
        <XmlExport />
        {can("fiscal.documento.cancelar") && <Inutilizacao />}
      </div>
    </div>
  );
}

function CancelRequests({
  rows,
  onOpen,
}: {
  rows: CancelRequestRow[];
  onOpen: (id: string) => void;
}) {
  const [note, setNote] = useState("");
  const decide = useFiscalAction<{ id: string; decision: "atender" | "recusar"; note?: string }>(
    (b) => `/pedidos-cancelamento/${b.id}/decidir`,
  );
  return (
    <Card title="Pedidos de cancelamento dos vendedores">
      <Table head={["Nota", "Cliente", "Total", "Motivo", "Pedido por", ""]}>
        {rows.map((r) => (
          <tr key={r.id}>
            <td className="px-3 py-2">
              <button className="text-primary underline" onClick={() => onOpen(r.documentId)}>
                {r.series}-{r.number}
              </button>
            </td>
            <td className="px-3 py-2">{r.recipientName}</td>
            <td className="text-right tabular-nums px-3 py-2">{formatMoney(r.totalAmount)}</td>
            <td className="px-3 py-2">{r.reason}</td>
            <td className="px-3 py-2">
              {r.requestedByEmail} {dateTimeBR(r.requestedAt)}
            </td>
            <td className="px-3 py-2 text-right whitespace-nowrap">
              <Button
                size="sm"
                variant="danger"
                onClick={() => decide.mutate({ id: r.id, decision: "atender" })}
              >
                Cancelar nota
              </Button>{" "}
              <Button
                size="sm"
                variant="secondary"
                disabled={note.trim().length < 3}
                onClick={() => decide.mutate({ id: r.id, decision: "recusar", note: note.trim() })}
              >
                Recusar
              </Button>
            </td>
          </tr>
        ))}
      </Table>
      <div className="mt-3 max-w-md">
        <Field label="Motivo da recusa (para recusar)">
          <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
        </Field>
      </div>
      <SaveError error={decide.error} />
    </Card>
  );
}

function XmlExport() {
  const [from, setFrom] = useState(today().slice(0, 8) + "01");
  const [to, setTo] = useState(today());
  const [error, setError] = useState<unknown>(null);
  return (
    <Card title="XMLs para a contabilidade">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="De">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="Até">
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
        <Button
          variant="secondary"
          onClick={() => {
            setError(null);
            apiDownload(`/fiscal/xml?from=${from}&to=${to}`, `xml-${from}-a-${to}.zip`).catch(
              setError,
            );
          }}
        >
          Baixar ZIP
        </Button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Notas autorizadas e canceladas do período, com os eventos.
      </p>
      <SaveError error={error} />
    </Card>
  );
}

function Inutilizacao() {
  const [form, setForm] = useState({
    model: "55",
    environment: "simulador",
    series: "1",
    from: "",
    to: "",
    reason: "",
  });
  const act = useFiscalAction<Record<string, unknown>>(
    () => "/inutilizacoes",
    "POST",
    () => setForm((f) => ({ ...f, from: "", to: "", reason: "" })),
  );
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <Card title="Inutilizar numeração">
      <Alert tone="info">
        Só números pulados ou de notas abandonadas. Número em uso nunca é inutilizado.
      </Alert>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Modelo">
          <Select value={form.model} onChange={set("model")}>
            <option value="55">55 — NF-e</option>
            <option value="65">65 — NFC-e</option>
          </Select>
        </Field>
        <Field label="Ambiente">
          <Select value={form.environment} onChange={set("environment")}>
            <option value="simulador">Simulador</option>
            <option value="homologacao">Homologação</option>
            <option value="producao">Produção</option>
          </Select>
        </Field>
        <Field label="Série">
          <Input inputMode="numeric" value={form.series} onChange={set("series")} />
        </Field>
        <Field label="Do número">
          <Input inputMode="numeric" value={form.from} onChange={set("from")} />
        </Field>
        <Field label="Ao número">
          <Input inputMode="numeric" value={form.to} onChange={set("to")} />
        </Field>
      </div>
      <div className="mt-3">
        <Field label="Justificativa (mínimo 15 caracteres)">
          <Input value={form.reason} onChange={set("reason")} maxLength={255} />
        </Field>
      </div>
      <Button
        className="mt-3"
        disabled={act.isPending || form.reason.trim().length < 15 || !form.from || !form.to}
        onClick={() =>
          act.mutate({
            model: form.model,
            environment: form.environment,
            series: Number(form.series),
            numberFrom: Number(form.from),
            numberTo: Number(form.to),
            reason: form.reason.trim(),
          })
        }
      >
        Inutilizar
      </Button>
      {act.isSuccess && <Alert tone="ok">Pedido enviado ao provedor.</Alert>}
      <SaveError error={act.error} />
    </Card>
  );
}
